import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useWiki,
  findCategoryParent,
  findCategoryInTree,
  getCategoryDescendantIds,
} from '../../contexts/WikiContext';
import { Plus, ListOrdered, Check } from 'lucide-react';
import SimpleModal from './SimpleModal';
import WikiCategoryCard from './WikiCategoryCard';
import WikiCategoryFormModal from './WikiCategoryFormModal';
import { useWikiAdminAuth } from './WikiAdminPanel';
import styles from './WikiCategoriesManager.module.css';

function ReorderDropZone({ onDrop, isActive }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (el && isActive && onDrop) {
      el._dropCallback = onDrop;
      return () => { el._dropCallback = null; };
    }
  }, [isActive, onDrop]);
  if (!isActive) return null;
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDrop?.();
  };
  return (
    <div
      ref={ref}
      className={styles.reorderDropZone}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      data-drop-zone="reorder"
    >
      Сюда для смены порядка
    </div>
  );
}

function CategoryTree({
  categories,
  parentId,
  onEdit,
  onDelete,
  onAddSubcategory,
  onOpenMoveModal,
  onReorder,
  draggedCategoryId,
  draggedCategory,
  draggedCategoryParentId,
  draggedDescendantIdSet,
  reorderMode,
  level = 0,
}) {
  const isSameLevelAsDragged =
    reorderMode &&
    Boolean(draggedCategoryId) &&
    (String(parentId ?? '') === String(draggedCategoryParentId ?? ''));

  return (
    <>
      {categories.map((cat, index) => (
        <React.Fragment key={cat.id}>
          <ReorderDropZone
            isActive={isSameLevelAsDragged}
            onDrop={() => onReorder?.dropAfter(index === 0 ? null : categories[index - 1], categories, parentId)}
          />
          <WikiCategoryCard
            category={cat}
            level={level}
            isDragging={String(draggedCategoryId) === String(cat.id)}
            siblings={categories}
            parentId={parentId}
            reorderMode={reorderMode}
            canDropAsChild={
              draggedCategory &&
              draggedDescendantIdSet &&
              String(cat.id) !== String(draggedCategory.id) &&
              !draggedDescendantIdSet.has(String(cat.id))
            }
            onEdit={onEdit}
            onDelete={onDelete}
            onAddSubcategory={onAddSubcategory}
            onDragStart={() => onReorder?.dragStart(cat)}
            onDropAfter={(afterCat) => onReorder?.dropAfter(afterCat, categories, parentId)}
            onDropAsChild={() => onReorder?.dropAsChild(cat)}
            onDragEnd={onReorder?.dragEnd}
            onOpenMoveModal={onOpenMoveModal}
          />
          {(cat.children || []).length > 0 && (
            <div className={styles.treeChildren}>
              <CategoryTree
                categories={cat.children}
                parentId={cat.id}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddSubcategory={onAddSubcategory}
                onOpenMoveModal={onOpenMoveModal}
                onReorder={onReorder}
                draggedCategoryId={draggedCategoryId}
                draggedCategory={draggedCategory}
                draggedCategoryParentId={draggedCategoryParentId}
                draggedDescendantIdSet={draggedDescendantIdSet}
                reorderMode={reorderMode}
                level={level + 1}
              />
            </div>
          )}
        </React.Fragment>
      ))}
      {categories.length > 0 && (
        <ReorderDropZone
          isActive={isSameLevelAsDragged}
          onDrop={() => onReorder?.dropAfter(categories[categories.length - 1], categories, parentId)}
        />
      )}
    </>
  );
}

const WikiCategoriesManager = () => {
  const {
    getCategories,
    getCategoriesTree,
    addCategory,
    updateCategory,
    deleteCategory,
    loadCategories,
  } = useWiki();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({ id: '', name: '', parentId: null });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderError, setReorderError] = useState('');
  const [touchMoveModalOpen, setTouchMoveModalOpen] = useState(false);
  const [touchMoveMode, setTouchMoveMode] = useState('after'); // 'after' | 'child'
  const [touchMoveTargetId, setTouchMoveTargetId] = useState(''); // afterCatId ('' => first) or childParentId
  const [touchMoveSaving, setTouchMoveSaving] = useState(false);
  const { onAuthError } = useWikiAdminAuth();

  const categoriesFlat = getCategories();
  const categoriesTree = getCategoriesTree();

  const handleOpenTouchMoveModal = useCallback((cat) => {
    setDraggedCategory(cat);
    setTouchMoveMode('after');
    setTouchMoveTargetId('');
    setTouchMoveSaving(false);
    setReorderError('');
    setTouchMoveModalOpen(true);
  }, []);

  const touchMoveAfterParent = useMemo(() => {
    if (!draggedCategory) return null;
    return findCategoryParent(categoriesTree, draggedCategory.id);
  }, [draggedCategory, categoriesTree]);

  const touchMoveAfterSiblings = useMemo(() => {
    if (!draggedCategory) return [];
    return touchMoveAfterParent ? touchMoveAfterParent.children || [] : categoriesTree;
  }, [draggedCategory, touchMoveAfterParent, categoriesTree]);

  const touchMoveChildOptions = useMemo(() => {
    if (!draggedCategory) return [];
    const excludeIds = new Set(getCategoryDescendantIds(draggedCategory).map(String));
    return categoriesFlat
      .filter((c) => String(c.id) !== String(draggedCategory.id) && !excludeIds.has(String(c.id)))
      .map((c) => findCategoryInTree(categoriesTree, c.id))
      .filter(Boolean);
  }, [draggedCategory, categoriesFlat, categoriesTree]);

  const draggedDescendantIdSet = useMemo(() => {
    if (!draggedCategory) return null;
    return new Set(getCategoryDescendantIds(draggedCategory).map(String));
  }, [draggedCategory]);

  const handleReorderDragStart = useCallback((cat) => {
    setDraggedCategory(cat);
    setReorderError('');
  }, []);
  const handleReorderDropAfter = useCallback(
    async (afterCat, siblings, parentId) => {
      if (!draggedCategory || !siblings) return;
      let newOrder;
      if (afterCat == null) {
        newOrder = 0;
      } else {
        const toIdx = siblings.findIndex((c) => String(c.id) === String(afterCat.id));
        if (toIdx === -1) return;
        newOrder = toIdx + 1;
      }
      const catToUpdate = draggedCategory;
      setDraggedCategory(null);
      try {
        setReorderError('');
        await updateCategory(catToUpdate.id, {
          name: catToUpdate.name,
          slug: catToUpdate.slug ?? catToUpdate.name,
          parentId: parentId ?? undefined,
          order: newOrder,
        });
        loadCategories();
      } catch (err) {
        if ((err.status === 401 || err.status === 403 || err.isAdminForbidden) && onAuthError) {
          onAuthError(err);
        } else {
          setReorderError(err?.message || 'Не удалось изменить порядок');
        }
      }
    },
    [draggedCategory, updateCategory, loadCategories]
  );
  const handleReorderDragEnd = useCallback(() => setDraggedCategory(null), []);

  const handleDropAsChild = useCallback(
    async (targetCategory) => {
      if (!draggedCategory || !targetCategory) return;
      const descendantIds = new Set(
        getCategoryDescendantIds(draggedCategory).map((id) => String(id))
      );
      if (descendantIds.has(String(targetCategory.id))) return;
      const catToUpdate = draggedCategory;
      const newOrder = (targetCategory.children || []).length;
      setDraggedCategory(null);
      try {
        setReorderError('');
        await updateCategory(catToUpdate.id, {
          name: catToUpdate.name,
          slug: catToUpdate.slug ?? catToUpdate.name,
          parentId: targetCategory.id,
          order: newOrder,
        });
        loadCategories();
      } catch (err) {
        if ((err.status === 401 || err.status === 403 || err.isAdminForbidden) && onAuthError) {
          onAuthError(err);
        } else {
          setReorderError(err?.message || 'Не удалось перенести в подкатегорию');
        }
      }
    },
    [draggedCategory, updateCategory, loadCategories]
  );

  const onReorder = useMemo(
    () => ({
      dragStart: handleReorderDragStart,
      dropAfter: handleReorderDropAfter,
      dropAsChild: handleDropAsChild,
      dragEnd: handleReorderDragEnd,
    }),
    [
      handleReorderDragStart,
      handleReorderDropAfter,
      handleDropAsChild,
      handleReorderDragEnd,
    ]
  );

  const parentOptions = useMemo(() => {
    if (!editingCategory) return categoriesFlat.map((c) => ({ id: c.id, name: c.name }));
    const excludeIds = new Set(
      getCategoryDescendantIds(editingCategory).map((id) => String(id))
    );
    return categoriesFlat
      .filter((c) => !excludeIds.has(String(c.id)))
      .map((c) => ({ id: c.id, name: c.name }));
  }, [editingCategory, categoriesFlat]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleCreate = (parentCategory = null) => {
    setEditingCategory(null);
    setFormData({
      id: '',
      name: '',
      parentId: parentCategory ? parentCategory.id : null,
    });
    setFormError('');
    setModalOpen(true);
  };

  const handleEdit = (cat) => {
    setEditingCategory(cat);
    const parent = findCategoryParent(categoriesTree, cat.id);
    setFormData({
      id: cat.id,
      name: cat.name,
      parentId: parent ? parent.id : null,
    });
    setFormError('');
    setModalOpen(true);
  };

  const handleSave = async () => {
    setFormError('');
    const id = String(formData.id ?? '').trim().toLowerCase().replace(/\s+/g, '_');
    const name = String(formData.name ?? '').trim();
    if (!name) {
      setFormError('Введите название категории');
      return;
    }
    if (!editingCategory && !id) {
      setFormError('Введите ID категории (латиница, без пробелов)');
      return;
    }
    const categoryId = editingCategory ? editingCategory.id : id;
    if (!editingCategory && categoriesFlat.some((c) => String(c.id) === String(categoryId))) {
      setFormError('Категория с таким ID уже существует');
      return;
    }
    setSaving(true);
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, {
          name,
          slug: editingCategory.slug,
          parentId: formData.parentId ?? undefined,
        });
      } else {
        await addCategory({
          id: categoryId,
          name,
          parentId: formData.parentId ?? undefined,
        });
      }
      setModalOpen(false);
      loadCategories();
    } catch (err) {
      if ((err.status === 401 || err.status === 403 || err.isAdminForbidden) && onAuthError) {
        onAuthError(err);
      } else {
        setFormError(err.message || 'Ошибка сохранения');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteError('');
    setDeleting(true);
    try {
      await deleteCategory(deleteConfirm.id);
      setDeleteConfirm(null);
      loadCategories();
    } catch (err) {
      if ((err.status === 401 || err.status === 403 || err.isAdminForbidden) && onAuthError) {
        onAuthError(err);
      } else {
        setDeleteError(err.message || 'Ошибка при удалении категории');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleTouchMoveCancel = () => {
    setTouchMoveModalOpen(false);
    setTouchMoveSaving(false);
    setTouchMoveTargetId('');
    setTouchMoveMode('after');
    setDraggedCategory(null);
    setReorderError('');
  };

  const handleTouchMoveConfirm = async () => {
    if (!draggedCategory) return;
    setTouchMoveSaving(true);
    try {
      if (touchMoveMode === 'after') {
        const parent = touchMoveAfterParent;
        const siblings = touchMoveAfterSiblings;
        const afterCat =
          touchMoveTargetId ? siblings.find((c) => String(c.id) === String(touchMoveTargetId)) : null;
        await handleReorderDropAfter(afterCat, siblings, parent ? parent.id : null);
      } else {
        const targetNode = touchMoveTargetId ? findCategoryInTree(categoriesTree, touchMoveTargetId) : null;
        await handleDropAsChild(targetNode);
      }
    } finally {
      setTouchMoveSaving(false);
      setTouchMoveModalOpen(false);
      setTouchMoveTargetId('');
      setTouchMoveMode('after');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Категории Вики</h1>
        <div className={styles.headerActions}>
          <div className={styles.orderControls}>
            {reorderMode ? (
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => { setReorderMode(false); setDraggedCategory(null); setReorderError(''); }}
              >
                <Check size={20} />
                Сохранить порядок
              </button>
            ) : (
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setReorderMode(true)}
              >
                <ListOrdered size={20} />
                Изменить порядок
              </button>
            )}
          </div>
          <button type="button" className={styles.primaryButton} onClick={handleCreate}>
            <Plus size={20} />
            Создать категорию
          </button>
        </div>
      </div>

      <p className={styles.subtitle}>
        Категории используются при создании и редактировании страниц Вики.
      </p>

      {reorderMode && reorderError && (
        <p className={styles.errorMessage} role="alert">
          {reorderError}
        </p>
      )}
      {categoriesTree.length > 0 ? (
        <div className={styles.list}>
          <CategoryTree
            categories={categoriesTree}
            parentId={null}
            onEdit={handleEdit}
            onDelete={setDeleteConfirm}
            onAddSubcategory={handleCreate}
            onOpenMoveModal={handleOpenTouchMoveModal}
            onReorder={onReorder}
            draggedCategoryId={draggedCategory?.id}
            draggedCategory={draggedCategory}
            draggedCategoryParentId={draggedCategory ? (findCategoryParent(categoriesTree, draggedCategory.id)?.id ?? null) : undefined}
            draggedDescendantIdSet={draggedDescendantIdSet}
            reorderMode={reorderMode}
            level={0}
          />
        </div>
      ) : (
        <div className={styles.emptyState}>
          <p>Категории еще не созданы</p>
          <button type="button" className={styles.primaryButton} onClick={handleCreate}>
            Создать первую категорию
          </button>
        </div>
      )}

      <WikiCategoryFormModal
        isOpen={modalOpen}
        isEdit={!!editingCategory}
        formData={formData}
        onFormChange={setFormData}
        parentOptions={parentOptions}
        error={formError}
        saving={saving}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />

      {touchMoveModalOpen && draggedCategory && (
        <SimpleModal
          title="Переместить категорию"
          onClose={handleTouchMoveCancel}
        >
          <div className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.label}>Как переместить?</div>
              <div className={styles.touchMoveModeButtons}>
                <button
                  type="button"
                  className={`${styles.touchMoveModeButton} ${touchMoveMode === 'after' ? styles.touchMoveModeButtonActive : ''}`}
                  onClick={() => setTouchMoveMode('after')}
                >
                  После
                </button>
                <button
                  type="button"
                  className={`${styles.touchMoveModeButton} ${touchMoveMode === 'child' ? styles.touchMoveModeButtonActive : ''}`}
                  onClick={() => setTouchMoveMode('child')}
                >
                  В подкатегории
                </button>
              </div>
            </div>

            <div className={styles.formRow}>
              <label className={styles.label}>
                {touchMoveMode === 'after' ? 'После какой категории?' : 'В какую категорию?'}
              </label>
              <select
                value={touchMoveTargetId ?? ''}
                onChange={(e) => setTouchMoveTargetId(e.target.value)}
                className={styles.select}
              >
                {touchMoveMode === 'after' ? (
                  <>
                    <option value="">Сначала (порядок 0)</option>
                    {touchMoveAfterSiblings
                      .filter((c) => String(c.id) !== String(draggedCategory.id))
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </>
                ) : (
                  <>
                    <option value="">Выберите категорию</option>
                    {touchMoveChildOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            <div className={styles.touchMoveHint}>
              {touchMoveMode === 'after'
                ? 'Категория останется в том же родителе; изменится только позиция.'
                : 'Категория станет дочерней для выбранной категории (в конец списка).'}
            </div>
          </div>

          <div className={styles.modalActions}>
            <button type="button" className={styles.secondaryButton} onClick={handleTouchMoveCancel} disabled={touchMoveSaving}>
              Отмена
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleTouchMoveConfirm}
              disabled={touchMoveSaving || (touchMoveMode === 'child' && !touchMoveTargetId)}
            >
              {touchMoveSaving ? 'Перемещение...' : 'Переместить'}
            </button>
          </div>
        </SimpleModal>
      )}

      {deleteConfirm && (
        <SimpleModal
          title="Удалить категорию"
          onClose={() => {
            setDeleteConfirm(null);
            setDeleteError('');
          }}
        >
          <p className={styles.deleteMessage}>
            Удалить категорию &quot;{deleteConfirm.name}&quot;? Страницы в этой категории не будут
            удалены, но останутся без категории.
          </p>
          {deleteError && (
            <p className={styles.errorMessage} role="alert">
              {deleteError}
            </p>
          )}
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setDeleteConfirm(null)}
            >
              Отмена
            </button>
            <button
              type="button"
              className={styles.dangerButton}
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Удаление...' : 'Удалить'}
            </button>
          </div>
        </SimpleModal>
      )}
    </div>
  );
};

export default WikiCategoriesManager;
